import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileArchive, Sparkles, Loader2, X, FileText, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { openBlob, errMsg } from './api';

const StoredFiles = ({ session, api, onChanged }) => {
  const open = async (f) => {
    try { openBlob(await api.fileBlob(session.id, f.id), f.content_type || undefined); }
    catch (e) { toast.error(errMsg(e, 'Pièce indisponible')); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Retirer « ${f.filename} » de la session ?`)) return;
    try { onChanged(await api.deleteFile(session.id, f.id)); toast.success('Pièce retirée'); }
    catch (e) { toast.error(errMsg(e, 'Suppression impossible')); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="ia-stored-files">
      {session.fichiers.map((f) => (
        <span key={f.id} className="inline-flex items-center gap-1.5 text-xs bg-slate-800 text-white rounded-full pl-3 pr-2 py-1">
          <button onClick={() => open(f)} className="inline-flex items-center gap-1 hover:underline" title={`${Math.round(f.size / 1024)} Ko — cliquer pour ouvrir`} data-testid={`ia-stored-file-${f.id}`}>
            <FileText className="h-3 w-3" /> {f.filename}
          </button>
          <button onClick={() => remove(f)} aria-label="Retirer la pièce" className="opacity-60 hover:opacity-100" data-testid={`ia-stored-file-delete-${f.id}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
};

export const UploadCard = ({ session, api, analysing, onAnalyse, onReanalyse, onChanged }) => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const add = (list) => setFiles((prev) => [...prev, ...Array.from(list || [])]);
  const stored = session?.fichiers?.length > 0;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="ia-upload-card">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Upload className="h-4 w-4 text-violet-600" /> 1. Documents du demandeur
        <span className="text-xs font-normal text-slate-400">(PDF, images, Word, TXT, ZIP…)</span>
      </h4>
      <input ref={inputRef} type="file" multiple accept=".pdf,.txt,.zip,.png,.jpg,.jpeg,.docx" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ''; }} data-testid="ia-file-input" />
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`rounded-lg border-2 border-dashed py-8 text-center cursor-pointer transition-colors ${drag ? 'border-violet-500 bg-violet-50' : 'border-violet-200 hover:border-violet-400'}`}
        data-testid="ia-dropzone"
      >
        {analysing ? <Loader2 className="h-6 w-6 mx-auto text-violet-500 animate-spin" /> : <FileArchive className="h-6 w-6 mx-auto text-violet-400" />}
        <p className="text-sm text-slate-500 mt-2">
          {stored ? 'Ajoutez des documents à cette session (multi-fichiers, ZIP accepté) puis ré-analysez' : 'Glissez-déposez ou cliquez pour ajouter des fichiers (plusieurs fichiers, ZIP accepté)'}
        </p>
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="ia-file-list">
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 text-xs bg-violet-100 text-violet-800 rounded-full px-3 py-1">
              {f.name}
              <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} aria-label="Retirer"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      {stored && <StoredFiles session={session} api={api} onChanged={onChanged} />}
      <div className="flex flex-wrap gap-2">
        {files.length > 0 && (
          <Button onClick={() => onAnalyse(files).then((ok) => ok && setFiles([]))} disabled={analysing} className="bg-violet-600 hover:bg-violet-700 text-white gap-2" data-testid="ia-analyse-btn">
            {analysing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {analysing ? "Analyse en cours… (2 à 3 min)" : stored ? 'Ajouter et ré-analyser tous les documents' : "Analyser avec l'IA"}
          </Button>
        )}
        {stored && files.length === 0 && (
          <Button onClick={onReanalyse} disabled={analysing} className="bg-violet-600 hover:bg-violet-700 text-white gap-2" data-testid="ia-reanalyse-btn">
            {analysing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            {analysing ? "Analyse en cours… (2 à 3 min)" : 'Ré-analyser complètement (tous les documents)'}
          </Button>
        )}
        {!stored && files.length === 0 && (
          <Button disabled className="bg-violet-600 text-white gap-2" data-testid="ia-analyse-btn"><Sparkles className="h-4 w-4" /> Analyser avec l'IA</Button>
        )}
      </div>
      {stored && <p className="text-[11px] text-slate-400">Une ré-analyse remplace les données extraites (vos corrections manuelles non enregistrées seront perdues).</p>}
    </section>
  );
};
